<template>
	<nav class="tabs-grouped">
		<template v-for="(groupTabs, group) in groupedTabs" :key="group">

			<!-- Группа с одним видимым табом — просто кнопка, без попапа -->
			<button
				v-if="groupTabs.all.length === 1"
				class="tab"
				:class="{ active: groupTabs.all[0].id === activeId }"
				@click="goToTab(groupTabs.all[0])"
			>
				<slot :tab="groupTabs.all[0]">
					<span class="tab-name">{{ groupTabs.all[0].displayName }}</span>
				</slot>
			</button>

			<!-- Группа с несколькими табами — только кнопка, попап снаружи v-for -->
			<button
				v-else
				:id="groupButtonId(group)"
				class="tab more"
				:class="{
					active: isGroupActive(groupTabs.all),
					open:   openedGroup === group,
				}"
				@mouseenter="onGroupEnter(group)"
				@mouseleave="onGroupLeave"
			>
				<VIcon class="more-arrow" material="keyboard_arrow_down" solid />
				<span class="tab-name">
					{{ activeTabInGroup(groupTabs.all)?.displayName ?? groupTabs.visible.displayGroupLabel }}
				</span>
			</button>

		</template>

		<!-- Один попап на все группы — показывает табы открытой группы -->
		<VPopup
			ref="popupRef"
			class="tabs-grouped-popup"
			:target="popupTarget"
			:visible="popupVisible"
			:offset="-2"
			align="left"
			@mouseleave="onPopupLeave"
			@close="hidePopup"
		>
			<button
				v-for="tab in activeGroupTabs"
				:key="tab.id"
				class="popup-tab"
				:class="{ active: tab.id === activeId }"
				@click="goToTab(tab)"
			>
				<slot :tab="tab">
					<span class="tab-name">{{ tab.displayName }}</span>
				</slot>
			</button>
		</VPopup>

	</nav>
</template>

<style lang="scss" src="./VTabsV2Grouped.scss" />

<script lang="ts">
import TabElementGrouped from './TabElementGrouped';

type GroupEntry = {
	visible: TabElementGrouped;        // таб с hidden: false (отображается на кнопке)
	all:     TabElementGrouped[];      // все табы группы (в попапе)
};

export default defineComponent({
	name: 'VTabsGrouped',

	props: {
		tabs: {
			type:    Array as PropType<TabElementGrouped[]>,
			default: () => [],
		},
		/**
		 * id активной вкладки. Совместим с `v-model:active-id` -
		 * в state-mode родитель держит активный id у себя и обновляет
		 * через эмит `update:activeId`. В router-mode (`baseUrl` задан)
		 * родитель обычно вычисляет из `$route.params`.
		 */
		activeId: {
			type:    String,
			default: null,
		},

		/**
		 * Базовый путь. Если задан - компонент работает в **router-mode**:
		 * клик по вкладке делает `$router.push('${baseUrl}/${tab.id}')`,
		 * а активный id родитель читает из `$route.params`.
		 *
		 * Если НЕ задан - компонент работает в **state-mode**:
		 * клик по вкладке эмитит `update:activeId`, ничего в роутер не пушит.
		 * Используется для табов внутри диалогов / страниц без URL-навигации.
		 */
		baseUrl: {
			type:    String,
			default: null,
		},
	},

	emits: ['update:activeId'],

	data() {
		return {
			openedGroup: null as string | null,
			popupTarget: null as string | null,
		};
	},

	computed: {
		popupVisible(): boolean {
			return typeof this.popupTarget === 'string';
		},

		/** Табы открытой группы — передаются в единственный попап */
		activeGroupTabs(): TabElementGrouped[] {
			if (!this.openedGroup) return [];
			return this.groupedTabs[this.openedGroup]?.all ?? [];
		},

		/**
		 * Группируем табы по полю group.
		 * Каждая группа: { visible: TabElement, all: TabElement[] }
		 */
		groupedTabs(): Record<string, GroupEntry> {
			const result: Record<string, GroupEntry> = {};

			for (const tab of this.tabs) {
				if (!tab.group) continue;
				if (!result[tab.group]) {
					result[tab.group] = { visible: null, all: [] };
				}
				result[tab.group].all.push(tab);
				if (!tab.hidden) {
					result[tab.group].visible = tab;
				}
			}

			return result;
		},
	},

	methods: {
		goToTab(tab: TabElementGrouped): void {
			this.hidePopup();

			if (tab.id === this.activeId) return;

			if (this.baseUrl !== null && this.baseUrl.length > 0) {
				this.$router.push({ path: `${this.baseUrl}/${tab.id}` });
			} else {
				this.$emit('update:activeId', tab.id);
			}
		},

		groupButtonId(group: string): string {
			return `tabs-group-${group}-${this.$.uid}`;
		},

		activeTabInGroup(tabs: TabElementGrouped[]): TabElementGrouped | null {
			return tabs.find(t => t.id === this.activeId) ?? null;
		},

		isGroupActive(tabs: TabElementGrouped[]): boolean {
			return this.activeTabInGroup(tabs) !== null;
		},


		// ============================================================
		//  Логика попапа, проверка координат курсора,
		//  чтобы предотвратить закрытие попапа пока курсор движется между
		//  кнопкой и попапом
		// ============================================================

		/** Навели на кнопку группы —
		 * запоминаем группу и в nextTick привязываем попап к кнопке
		 */
		onGroupEnter(group: string): void {
			this.openedGroup = group;
			this.$nextTick().then(() => {
				this.popupTarget = `#${this.groupButtonId(group)}`;
			});
		},

		/**
		 * Скрываем попап.
		 * popupTarget сбрасывается сразу — попап начинает закрываться.
		 * openedGroup сбрасывается через 150мс — чтобы попап успел анимированно исчезнуть
		 * прежде чем его содержимое очистится.
		 */
		hidePopup(): void {
			this.popupTarget = null;
			this.$nextTick().then(() => {
				setTimeout(() => {
					this.openedGroup = null;
				}, 150);
			});
		},

		/**
		 * Увели курсор с кнопки группы.
		 * Проверяем координаты — если курсор перешёл на попап, не закрываем.
		 * Это позволяет пользователю плавно переместить мышь с кнопки на попап
		 * без случайного закрытия.
		 */
		onGroupLeave(event: MouseEvent): void {
			if (!this.popupVisible) return;

			const x        = event.clientX || event.pageX;
			const y        = event.clientY || event.pageY;
			const targetEl = this.$el?.querySelector(this.popupTarget);
			const hoverEl  = document.elementFromPoint(x, y);
			const popupEl  = (this.$refs.popupRef as any)?.$el ?? this.$refs.popupRef;

			if (!targetEl?.contains(hoverEl) && !popupEl?.contains(hoverEl)) {
				this.hidePopup();
			}
		},

		/**
		 * Увели курсор с попапа.
		 * Аналогично onGroupLeave — проверяем не вернулся ли курсор на кнопку группы,
		 * чтобы не закрывать попап при движении между попапом и кнопкой.
		 */
		onPopupLeave(event: MouseEvent): void {
			if (!this.popupVisible) return;

			const x        = event.clientX || event.pageX;
			const y        = event.clientY || event.pageY;
			const targetEl = this.$el?.querySelector(this.popupTarget);
			const hoverEl  = document.elementFromPoint(x, y);
			const popupEl  = (this.$refs.popupRef as any)?.$el ?? this.$refs.popupRef;

			if (!targetEl?.contains(hoverEl) && !popupEl?.contains(hoverEl)) {
				this.hidePopup();
			}
		},
	},
});
</script>
